/**
 * What media the questionnaire asset intakes accept: the one upload policy in
 * shared-types (B23), which the upload route reads too. The ConceptAssetsPanel
 * file input, the ComposerCollectIntake pickers and the builder-chat composer
 * paste/drop gate all read it, so a file one surface accepts is never refused
 * by another, or by the server, for a reason the user cannot see.
 */

import {
  QUESTIONNAIRE_ASSET_UPLOAD_MIME_TYPES,
  isQuestionnaireAssetUploadMime,
  normalizeUploadMime,
  questionnaireAssetUploadMaxBytes,
  questionnaireAssetUploadRefusal,
} from "@digital-twins/shared-types";
import i18n from "@/i18n";
import { isSupportedVideoMime } from "@/components/chat/useChatFileIngest";

export { isSupportedVideoMime };

/** <input accept> value for every asset-media picker. */
export const ASSET_MEDIA_ACCEPT = QUESTIONNAIRE_ASSET_UPLOAD_MIME_TYPES.join(",");

/** True when a picked file can become a tested-asset media ref (collect intakes). */
export function isAcceptedAssetFile(file: File): boolean {
  return isQuestionnaireAssetUploadMime(file.type);
}

/**
 * The line saying why `file` cannot be uploaded (its type, or its size for
 * its kind), in the interface language; null when the policy takes it. The
 * builder checks before it sends the bytes.
 */
export function assetUploadRefusalLine(file: File): string | null {
  const refusal = questionnaireAssetUploadRefusal(file);
  if (refusal === null) return null;
  return i18n.t(`questionnaire.upload.${refusal}`, {
    ns: "app",
    name: file.name,
    maxMb: Math.round(questionnaireAssetUploadMaxBytes(file.type) / (1024 * 1024)),
  });
}

/**
 * Composer paste/drop gate (QuestionnaireBuilderChat). PDFs stay collect-only:
 * the composer confirm card only offers landing page / ad, whose stimuli are
 * images or videos.
 * - 'ignore': no files in the event, so the default text paste proceeds.
 * - 'unsupported': files were given but none are usable; the caller owes the
 *   user feedback (never a silent drop).
 * - 'media': the usable subset to stage on the asset-confirm card.
 */
export function gateComposerMediaFiles(
  files: Array<File>,
):
  | { kind: "ignore" }
  | { kind: "unsupported" }
  | { kind: "media"; media: Array<File> } {
  if (files.length === 0) return { kind: "ignore" };
  const media = files.filter(
    (file) =>
      isQuestionnaireAssetUploadMime(file.type) &&
      normalizeUploadMime(file.type) !== "application/pdf",
  );
  return media.length === 0 ? { kind: "unsupported" } : { kind: "media", media };
}
